import { LINEAR_CURVE } from '../../core/params/curve';
import { DEFAULT_PALETTE } from '../../core/palette';
import { SHAPE_OPTIONS } from '../../core/shapes';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const POISSON_SCATTER_PARAMS = [
  {
    kind: 'float',
    key: 'spacing',
    label: 'Mindestabstand',
    min: 3,
    max: 200,
    step: 0.5,
    scale: 'log',
    default: 22,
    unit: 'px',
    bulkRange: [8, 70],
    group: 'Verteilung',
    tooltip: 'Kleinster erlaubter Abstand zwischen zwei Elementen.',
  },
  {
    kind: 'float',
    key: 'gradient',
    label: 'Dichtegradient',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.55,
    group: 'Verteilung',
    tooltip: 'Verkleinert den Abstand nahe am Text — 0 verteilt völlig gleichmässig.',
  },
  {
    kind: 'float',
    key: 'gradientReach',
    label: 'Gradient-Reichweite',
    min: 20,
    max: 900,
    step: 5,
    scale: 'log',
    default: 260,
    unit: 'px',
    group: 'Verteilung',
    showIf: (v) => (v.gradient as number) > 0,
  },
  {
    kind: 'int',
    key: 'attempts',
    label: 'Versuche pro Punkt',
    min: 4,
    max: 60,
    step: 1,
    default: 22,
    neverRandomise: true,
    group: 'Verteilung',
    tooltip: 'Höher füllt dichter, kostet aber Rechenzeit. Bridsons k-Parameter.',
  },
  {
    kind: 'float',
    key: 'exclusion',
    label: 'Ausschlusszone',
    min: -40,
    max: 200,
    step: 1,
    default: 14,
    unit: 'px',
    bulkRange: [0, 80],
    group: 'Verteilung',
    tooltip: 'Abstand, den Elemente zum Text halten. Negativ lässt sie überlappen.',
  },
  {
    kind: 'enum',
    key: 'region',
    label: 'Bereich',
    default: 'outside',
    segmented: true,
    group: 'Verteilung',
    options: [
      { value: 'outside', label: 'Aussen' },
      { value: 'inside', label: 'Innen' },
      { value: 'all', label: 'Überall' },
    ],
  },

  {
    kind: 'enum',
    key: 'shape',
    label: 'Element',
    default: 'circle',
    group: 'Element',
    options: SHAPE_OPTIONS,
  },
  {
    kind: 'int',
    key: 'starPoints',
    label: 'Zacken',
    min: 3,
    max: 16,
    step: 1,
    default: 5,
    group: 'Element',
    showIf: (v) => v.shape === 'star' || v.shape === 'mixed',
  },
  {
    kind: 'range',
    key: 'size',
    label: 'Grösse',
    min: 0.5,
    max: 120,
    step: 0.5,
    default: [2, 9],
    unit: 'px',
    precision: 1,
    group: 'Element',
  },
  {
    kind: 'curve',
    key: 'sizeByDistance',
    label: 'Grösse nach Distanz',
    default: LINEAR_CURVE,
    xLabel: 'nah',
    yLabel: 'fern',
    group: 'Element',
  },
  {
    kind: 'float',
    key: 'rotation',
    label: 'Rotation',
    min: -180,
    max: 180,
    step: 1,
    default: 0,
    unit: '°',
    group: 'Element',
    showIf: (v) => v.shape !== 'circle',
  },
  {
    kind: 'float',
    key: 'rotationJitter',
    label: 'Rotationsstreuung',
    min: 0,
    max: 180,
    step: 1,
    default: 0,
    unit: '°',
    group: 'Element',
    showIf: (v) => v.shape !== 'circle',
  },
  {
    kind: 'bool',
    key: 'alignToText',
    label: 'An der Kontur ausrichten',
    default: false,
    group: 'Element',
    tooltip: 'Dreht jedes Element entlang der Feldrichtung — die Streuung folgt dann den Buchstaben.',
    showIf: (v) => v.shape !== 'circle',
  },

  {
    kind: 'enum',
    key: 'style',
    label: 'Stil',
    default: 'fill',
    segmented: true,
    group: 'Darstellung',
    options: [
      { value: 'fill', label: 'Füllung' },
      { value: 'stroke', label: 'Kontur' },
    ],
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Strichstärke',
    min: 0.2,
    max: 8,
    step: 0.1,
    default: 1,
    unit: 'px',
    group: 'Darstellung',
    showIf: (v) => v.style === 'stroke',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.9,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'distance',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'size', label: 'Nach Grösse' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'riso' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type PoissonScatterValues = SchemaValues<typeof POISSON_SCATTER_PARAMS>;
